import { CircularProgress, MenuItem, TextField, type TextFieldProps } from '@mui/material';
import { forwardRef, useEffect } from 'react';

import { useProfesionales } from '@/features/profesionales/hooks/useProfesionales';

interface ProfesionalSelectProps extends Omit<TextFieldProps, 'select' | 'children'> {
  emptyLabel?: string;
  /** When true, renders a selectable empty option (value="") for filter use-cases. */
  includeEmpty?: boolean;
}

/**
 * Reusable profesional selector. Loads all profesionales sorted by name.
 * Compatible with react-hook-form via spread of register() or Controller.
 * Assumes low volume (tens, not hundreds). Migrate to Autocomplete if needed.
 */
export const ProfesionalSelect = forwardRef<HTMLDivElement, ProfesionalSelectProps>(
  function ProfesionalSelect(
    { emptyLabel = 'Seleccionar profesional', helperText, includeEmpty = false, ...textFieldProps },
    ref
  ) {
    const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
      useProfesionales({
        sort_by: 'nombre_apellido',
        sort_order: 'asc',
        limit: 100,
      });

    useEffect(() => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const isLoadingAll = isLoading || hasNextPage;

    return (
      <TextField
        select
        ref={ref}
        fullWidth
        helperText={
          isLoadingAll && !textFieldProps.error ? 'Cargando profesionales...' : helperText
        }
        InputProps={{
          endAdornment: isLoadingAll ? (
            <CircularProgress size={16} sx={{ mr: 3 }} />
          ) : undefined,
        }}
        SelectProps={{ displayEmpty: false }}
        {...textFieldProps}
      >
        <MenuItem value="" disabled={!includeEmpty}>
          <em>{emptyLabel}</em>
        </MenuItem>
        {items.map((p) => (
          <MenuItem key={p.uid_profesional} value={p.uid_profesional}>
            {p.nombre_apellido}
          </MenuItem>
        ))}
      </TextField>
    );
  }
);
